import { ArdoqId } from '@ardoq/api-types';
import {
  TagContainerWithTruncation,
  type OverflowBadgeProps,
} from '@ardoq/snowflakes';
import { EnhancedScopeData } from '@ardoq/data-model';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { ComponentTag } from './ComponentTag';
import { ComponentSuggestionWithBreadcrumbs } from '@ardoq/suggestions-api';
import { KeyboardEventHandler, useRef } from 'react';
import { Tag } from '@ardoq/status-ui';
import { type I18nLocale, t } from '@ardoq/i18n';
import {
  ComponentTypeContainer,
  ComponentTypeLabel,
} from '@ardoq/basic-components';
import { colors } from '@ardoq/design-tokens';

const OverflowBadge = ({ hiddenCount }: OverflowBadgeProps) => (
  <ComponentTypeContainer
    $borderColor={colors.borderAction}
    $backgroundColor={colors.surfaceStatusNeutral}
    $isSelected={false}
    $noMargin
  >
    <ComponentTypeLabel $color={colors.textDefault}>
      {`+${hiddenCount} more`}
    </ComponentTypeLabel>
  </ComponentTypeContainer>
);

type ComponentTagsProps = {
  componentIds: ArdoqId[];
  scopeData: EnhancedScopeData;
  getOpenComponentOverview?: (componentId: ArdoqId) => VoidFunction | undefined;
  onCloseClick?: (componentId: ArdoqId) => void;
  componentSuggestions?: Array<ComponentSuggestionWithBreadcrumbs>;
  getOnBackspaceClick?: (
    componentId: ArdoqId
  ) => KeyboardEventHandler<HTMLElement>;
  canEditReference?: (componentId: ArdoqId) => boolean;
  i18nLocale: I18nLocale;
};

const getRepresentationData = (
  componentId: ArdoqId,
  scopeData: EnhancedScopeData,
  componentSuggestions?: Array<ComponentSuggestionWithBreadcrumbs>
) => {
  const component = scopeData.componentsById[componentId];
  if (component) {
    const representationData =
      enhancedScopeDataOperations.getComponentRepresentationData(
        scopeData,
        componentId
      );

    const color = enhancedScopeDataOperations.getComponentColor(
      scopeData,
      componentId
    );
    return representationData.isImage
      ? {
          name: component.name,
          level: 0,
          icon: undefined,
          image: representationData.value,
          color,
        }
      : {
          name: component.name,
          level: 0,
          icon: representationData.value,
          image: undefined,
          color,
        };
  }
  if (componentSuggestions) {
    const suggestion = componentSuggestions.find(
      suggestion => suggestion.value === componentId
    );
    if (suggestion) {
      return {
        name: suggestion.label,
        level: 0,
        icon: suggestion.style.icon,
        image: suggestion.style.image,
        color: suggestion.style.color,
      };
    }
  }
};

export const ComponentTags = ({
  componentIds,
  getOpenComponentOverview,
  scopeData,
  onCloseClick,
  componentSuggestions,
  getOnBackspaceClick,
  canEditReference,
  i18nLocale,
}: ComponentTagsProps) => {
  const tagContainerRef = useRef<{ closePopover: VoidFunction }>(null);

  return (
    <TagContainerWithTruncation
      ref={tagContainerRef}
      maxHeight="calc(2rem + 20px)" // two rows of tag height + padding
      OverflowBadge={OverflowBadge}
    >
      {componentIds.map((componentId, i) => {
        const representationData = getRepresentationData(
          componentId,
          scopeData,
          componentSuggestions
        );

        if (!representationData) {
          return (
            <Tag key={i}>
              {t(
                i18nLocale,
                'inventoryApp:ag.cellRenderer.loading',
                'Loading…'
              )}
            </Tag>
          );
        }

        const openComponentOverview = getOpenComponentOverview?.(componentId);

        return (
          <ComponentTag
            key={i}
            name={representationData.name}
            style={representationData}
            showComponentOverviewDrawer={
              openComponentOverview
                ? () => {
                    openComponentOverview();
                    tagContainerRef.current?.closePopover();
                  }
                : undefined
            }
            onCloseClick={
              onCloseClick && canEditReference?.(componentId)
                ? () => onCloseClick(componentId)
                : undefined
            }
            onBackspaceClick={
              getOnBackspaceClick && canEditReference?.(componentId)
                ? getOnBackspaceClick(componentId)
                : undefined
            }
          />
        );
      })}
    </TagContainerWithTruncation>
  );
};
